/**
 * Encoder GS1 per EAN-13 ed EAN-8 (specifiche GS1 General Specifications, §5.2).
 *
 * Nessuna dipendenza: output puro come array di moduli (`true` = barra,
 * `false` = spazio). EAN-13 → 95 moduli, EAN-8 → 67 moduli
 * (guard 3 + 6×7 + guard centrale 5 + 6×7 + guard 3, e idem con 4×7).
 */

/** Larghezza nominale EAN-13 al 100% di ingrandimento: 37,29 mm (113X). */
export const EAN13_NOMINAL_WIDTH_MM = 37.29;

/** Modulo X nominale al 100% di ingrandimento: 0,33 mm. */
export const EAN_MODULE_NOMINAL_MM = 0.33;

/** Modulo X minimo stampabile (80% di ingrandimento): 0,264 mm. */
export const EAN_MODULE_MIN_MM = 0.264;

/** Modulo QR minimo stampabile raccomandato: 0,33 mm. */
export const QR_MODULE_MIN_MM = 0.33;

/** Moduli di un simbolo EAN-13. */
export type Ean13Modules = boolean[] & { readonly length: 95 };

/** Moduli di un simbolo EAN-8. */
export type Ean8Modules = boolean[] & { readonly length: 67 };

// Number set L (dispari): prima cifra EAN-13, cifre EAN-8, metà destra EAN-13.
const SET_L = [
  '0001101', // 0
  '0011001', // 1
  '0010011', // 2
  '0111101', // 3
  '0100011', // 4
  '0110001', // 5
  '0101111', // 6
  '0111011', // 7
  '0110111', // 8
  '0001011', // 9
] as const;

// Number set G (pari): complemento del set R, usato per il parity EAN-13.
const SET_G = [
  '0100111', // 0
  '0110011', // 1
  '0011011', // 2
  '0100001', // 3
  '0011101', // 4
  '0111001', // 5
  '0000101', // 6
  '0010001', // 7
  '0001001', // 8
  '0010111', // 9
] as const;

// Number set R (complemento di L): metà destra EAN-13.
const SET_R = [
  '1110010', // 0
  '1100110', // 1
  '1101100', // 2
  '1000010', // 3
  '1011100', // 4
  '1001110', // 5
  '1010000', // 6
  '1000100', // 7
  '1001000', // 8
  '1110100', // 9
] as const;

// Parity pattern delle sei cifre della metà sinistra EAN-13, indicizzato
// dalla tredicesima cifra implicita (la prima del GTIN).
const PARITY_13: ReadonlyArray<readonly ('L' | 'G')[]> = [
  ['L', 'L', 'L', 'L', 'L', 'L'], // 0
  ['L', 'L', 'G', 'L', 'G', 'G'], // 1
  ['L', 'L', 'G', 'G', 'L', 'G'], // 2
  ['L', 'L', 'G', 'G', 'G', 'L'], // 3
  ['L', 'G', 'L', 'L', 'G', 'G'], // 4
  ['L', 'G', 'G', 'L', 'L', 'G'], // 5
  ['L', 'G', 'G', 'G', 'L', 'L'], // 6
  ['L', 'G', 'L', 'G', 'L', 'G'], // 7
  ['L', 'G', 'L', 'G', 'G', 'L'], // 8
  ['L', 'G', 'G', 'L', 'G', 'L'], // 9
];

const LEFT_GUARD = [true, false, true];
const CENTRE_GUARD = [false, true, false, true, false];

const assertDigits = (value: string): void => {
  if (!/^\d+$/.test(value)) {
    throw new Error(`GTIN non valido: attese solo cifre, ricevuto "${value}"`);
  }
};

/**
 * Calcola la cifra di controllo GS1 per un GTIN **senza** check digit
 * (7 cifre per EAN-8, 12 per EAN-13, 13/17 per GTIN-14/SSCC).
 * Pesi alternati 3,1,3,... partendo dalla cifra più a destra dei dati.
 */
export function gtinCheckDigit(dataDigits: string): number {
  assertDigits(dataDigits);
  let sum = 0;
  let weight = 3;
  for (let i = dataDigits.length - 1; i >= 0; i--) {
    sum += (dataDigits.charCodeAt(i) - 48) * weight;
    weight = weight === 3 ? 1 : 3;
  }
  return (10 - (sum % 10)) % 10;
}

/** Lunghezze GTIN valide (GS1): EAN-8, UPC-A, EAN-13, GTIN-14. */
const GTIN_LENGTHS = [8, 12, 13, 14];

/** Verifica un GTIN completo (ultima cifra = check digit). */
export function isValidGtin(gtin: string): boolean {
  if (!GTIN_LENGTHS.includes(gtin.length)) {
    return false;
  }
  const data = gtin.slice(0, -1);
  const check = gtin.slice(-1);
  if (!/^\d+$/.test(check)) {
    return false;
  }
  try {
    return gtinCheckDigit(data) === Number(check);
  } catch {
    return false;
  }
}

const assertGtinOfLength = (gtin: string, length: number, kind: 'EAN-13' | 'EAN-8'): void => {
  assertDigits(gtin);
  if (gtin.length !== length) {
    throw new Error(`${kind} non valido: attese ${length} cifre, ricevute ${gtin.length}`);
  }
  const expected = gtinCheckDigit(gtin.slice(0, -1));
  if (expected !== Number(gtin.slice(-1))) {
    throw new Error(
      `${kind} non valido: check digit atteso ${expected}, ricevuto ${gtin.slice(-1)}`,
    );
  }
};

const digitModules = (digit: number, set: ReadonlyArray<string>): boolean[] =>
  set[digit].split('').map((bit) => bit === '1');

/**
 * Codifica un EAN-13 completo (13 cifre, check digit verificato) in 95 moduli:
 * guard 101 + 6 caratteri sinistra (parity dalla prima cifra) + guard
 * centrale 01010 + 6 caratteri destra in set R + guard 101.
 */
export function encodeEan13(gtin13: string): Ean13Modules {
  assertGtinOfLength(gtin13, 13, 'EAN-13');
  const digits = gtin13.split('').map(Number);
  const parity = PARITY_13[digits[0]];

  const modules: boolean[] = [...LEFT_GUARD];
  for (let i = 1; i <= 6; i++) {
    const set = parity[i - 1] === 'L' ? SET_L : SET_G;
    modules.push(...digitModules(digits[i], set));
  }
  modules.push(...CENTRE_GUARD);
  for (let i = 7; i <= 12; i++) {
    modules.push(...digitModules(digits[i], SET_R));
  }
  modules.push(...LEFT_GUARD);
  return modules as Ean13Modules;
}

/**
 * Codifica un EAN-8 completo (8 cifre, check digit verificato) in 67 moduli:
 * guard 101 + 4 caratteri in set L + guard centrale 01010 + 4 caratteri in
 * set R + guard 101.
 */
export function encodeEan8(gtin8: string): Ean8Modules {
  assertGtinOfLength(gtin8, 8, 'EAN-8');
  const digits = gtin8.split('').map(Number);

  const modules: boolean[] = [...LEFT_GUARD];
  for (let i = 0; i < 4; i++) {
    modules.push(...digitModules(digits[i], SET_L));
  }
  modules.push(...CENTRE_GUARD);
  for (let i = 4; i < 8; i++) {
    modules.push(...digitModules(digits[i], SET_R));
  }
  modules.push(...LEFT_GUARD);
  return modules as Ean8Modules;
}

/**
 * Segmenti del testo leggibile (HRI) sotto le barre, per kind:
 * EAN-13 → `[prima cifra, 6 cifre sinistra, 6 cifre destra]`
 * (la prima cifra sta a sinistra del guard, fuori dalle barre);
 * EAN-8 → `[4 cifre sinistra, 4 cifre destra]`.
 * Solleva su GTIN non valido o lunghezza non EAN-13/EAN-8.
 */
export function humanReadableSegments(gtin: string): string[] {
  if (gtin.length === 13) {
    assertGtinOfLength(gtin, 13, 'EAN-13');
    return [gtin.slice(0, 1), gtin.slice(1, 7), gtin.slice(7, 13)];
  }
  if (gtin.length === 8) {
    assertGtinOfLength(gtin, 8, 'EAN-8');
    return [gtin.slice(0, 4), gtin.slice(4, 8)];
  }
  throw new Error(`GTIN non valido per HRI: lunghezza ${gtin.length} (attese 13 o 8 cifre)`);
}

/** Altezza delle cifre HRI al 100% di ingrandimento: ≈ 3,08 mm sul simbolo
 *  nominale alto 25,93 mm. Scala con l'ingrandimento, non con l'altezza. */
export const EAN13_HRI_HEIGHT_MM = 3.08;

/** La fascia HRI non supera mai questa frazione dell'altezza dell'asset:
 *  a ingrandimento pieno con asset schiacciato le cifre restano leggibili. */
export const BARCODE_HRI_MAX_ZONE_RATIO = 0.4;

/** Frazione della fascia HRI lasciata come respiro sotto le barre, sopra la
 *  baseline delle cifre (il resto è il corpo del carattere). */
export const BARCODE_HRI_BASELINE_PAD_RATIO = 0.15;

/**
 * Altezza della fascia delle cifre HRI sotto le barre, in mm. La cifra GS1
 * scala con l'ingrandimento (larghezza / nominale EAN-13), non con
 * l'altezza dell'asset — che l'operatore può troncare. Canvas e painter PDF
 * la condividono: è il contratto WYSIWYG tra editor e stampa.
 */
export function barcodeHriZoneMm(widthMm: number, heightMm: number): number {
  return Math.min(
    EAN13_HRI_HEIGHT_MM * (widthMm / EAN13_NOMINAL_WIDTH_MM),
    heightMm * BARCODE_HRI_MAX_ZONE_RATIO,
  );
}
